// Offre « Changement de créneau » : la famille d'un élève du tuteur change l'horaire d'un
// de ses cours, et le tuteur garde l'élève en choisissant `pick` créneaux parmi ceux que
// l'offre réserve. Ce fichier ne porte que la logique pure de la carte et de la modale de
// l'écran Propositions (libellés, sélection, corps de la réponse). Les horaires se lisent
// dans le `label` que l'offre porte sur chaque cours et chaque créneau (« jeudi 18h », à
// l'heure du lecteur) : rien ne les reformate ici.

import {
    capitalizeFirst,
    joinFrench,
    numberInWords,
} from "../../ports/primary/ui/formatters/textFormatter"

export const SLOT_CHANGE_KIND = "slot_change"

/** Les créneaux de l'offre se choisissent dans l'onglet Propositions, pas depuis l'offre de groupe. */
export const SLOT_CHANGE_CHOOSE_IN_PROPOSALS =
    "Choisis les créneaux que tu gardes dans l'onglet « Propositions »."

export const isSlotChangeProposal = (item) =>
    item?.group_attribution?.kind === SLOT_CHANGE_KIND &&
    !!item?.group_attribution?.slot_change

export const slotChangeOf = (item) => item?.group_attribution?.slot_change ?? {}

/** « mercredi 17h » → « Mercredi 17h » — un créneau en tête de ligne. */
export const slotLabel = (slot) => capitalizeFirst(slot?.label ?? "")

const pickOf = (offer) => Math.max(0, Number(offer?.pick) || 0)
const slotsOf = (offer) => (Array.isArray(offer?.slots) ? offer.slots : [])
const coursesOf = (offer) => (Array.isArray(offer?.courses) ? offer.courses : [])
const keptOf = (offer) => (Array.isArray(offer?.kept) ? offer.kept : [])
// Les horaires du lot qu'un autre tuteur a déjà acceptés : absents d'une offre plus ancienne.
const takenOf = (offer) =>
    (Array.isArray(offer?.taken_slots) ? offer.taken_slots : []).filter(
        (slot) => !!slot?.label
    )

/** Distinct subject names of the courses that move, in their order. */
export const subjectNames = (offer) => {
    const names = []
    coursesOf(offer).forEach((course) =>
        (course?.subjects ?? []).forEach((subject) => {
            if (subject?.name && !names.includes(subject.name))
                names.push(subject.name)
        })
    )
    return names
}

const lowerSubjects = (offer) =>
    joinFrench(subjectNames(offer).map((name) => name.toLowerCase()))

export const slotsLabel = (offer) =>
    slotsOf(offer)
        .map(slotLabel)
        .join(", ")

export const cardTitle = (offer) => {
    const student = offer?.student
    const className = student?.class ? ` (${student.class})` : ""
    const who = student?.firstname ? ` : ${student.firstname}${className}` : ""
    const count = coursesOf(offer).length
    return `Changement de créneau${who}${count > 1 ? ` · ${count} cours` : ""}`
}

export const cardInfoLines = (offer) => {
    const pick = pickOf(offer)
    // Un seul créneau à prendre se lit dans la liste ; plusieurs se comptent.
    const choosesSeveral = pick > 1 && slotsOf(offer).length > pick
    const lines = [
        [subjectNames(offer).join(", "), "1 élève", offer?.student?.class]
            .filter(Boolean)
            .join(" · "),
        choosesSeveral
            ? `${pick} créneaux à choisir parmi : ${slotsLabel(offer)}`
            : `Créneaux proposés : ${slotsLabel(offer)}`,
    ]
    if (offer?.effective_label)
        lines.push(`Toutes les semaines à partir du ${offer.effective_label}`)
    return lines
}

export const dialogTitle = (offer) => {
    const firstname = offer?.student?.firstname ?? "ton élève"
    const subjects = lowerSubjects(offer)
    return `Garder ${firstname}${subjects ? ` en ${subjects}` : ""} ?`
}

export const dialogIntro = (offer) => {
    const pick = pickOf(offer)
    const courses = coursesOf(offer)
    const choice = pick === 1 ? "le créneau" : `${pick} créneaux`
    const from = offer?.effective_label ? `, chaque semaine dès le ${offer.effective_label}` : ""
    if (courses.length > 1)
        return `Ses ${numberInWords(courses.length)} cours de ${lowerSubjects(
            offer
        )} changent. Choisis ${choice}${from} :`
    return `Sa famille déplace le ${courses[0]?.label ?? ""}. Choisis ${choice}${from} :`
}

/**
 * Ce que le tuteur doit savoir avant d'accepter, en une note : les cours qu'il garde, puis soit
 * que plusieurs créneaux vont ensemble, soit qu'un autre tuteur peut prendre l'offre.
 */
export const dialogNote = (offer) => {
    const pick = pickOf(offer)
    const kept = keptOf(offer).map((course) => course?.label).filter(Boolean)
    const notes = []
    if (kept.length > 0)
        notes.push(
            `Tu gardes aussi ${kept.length > 1 ? "ses" : "son"} ${joinFrench(kept)}.`
        )
    if (pick > 1) {
        const count = numberInWords(pick)
        notes.push(`Les ${count} cours vont ensemble.`)
    } else {
        notes.push("Accepte avant qu'un autre tuteur ne commence ce cours.")
    }
    return notes.join(" ")
}

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]

// « Wednesday 17:00 » → « 3 17:00 », pour ranger les horaires dans la semaine ; ce qui ne se
// lit pas passe après.
const weekOrder = (dayTime) => {
    const [, day, hour, minute] =
        /^(\w+)\s+(\d{1,2}):(\d{2})/.exec(String(dayTime ?? "").trim()) ?? []
    const index = WEEKDAYS.indexOf(day)
    return index < 0 ? "9" : `${index + 1} ${hour.padStart(2, "0")}:${minute}`
}

/**
 * Les lignes de la modale, dans l'ordre de la semaine : les créneaux à choisir, et parmi eux
 * ceux du lot qu'un autre tuteur a déjà pris (« Mercredi 17h · déjà pris »), qui ne se
 * choisissent plus.
 */
export const dialogOptions = (offer) => {
    const choosable = slotsOf(offer).map((slot) => ({
        key: `slot-${slot.live_session_id}`,
        liveSessionId: slot.live_session_id,
        label: slotLabel(slot),
        // Ses autres cours ce jour-là, dits par le serveur : ce que le créneau donne dans sa journée.
        note: slot.day_note || null,
        taken: false,
        order: weekOrder(slot.day_time),
    }))
    const offered = new Set(choosable.map((option) => option.order))
    const taken = takenOf(offer)
        .map((slot) => ({
            key: `taken-${slot.key ?? slot.label}`,
            liveSessionId: null,
            label: `${capitalizeFirst(slot.label)} · déjà pris`,
            note: null,
            taken: true,
            order: weekOrder(slot.key),
        }))
        .filter((option) => option.order === "9" || !offered.has(option.order))
    // Tri stable : à égalité, l'ordre de l'offre.
    return [...choosable, ...taken].sort((a, b) =>
        a.order < b.order ? -1 : a.order > b.order ? 1 : 0
    )
}

export const acceptButtonLabel = (pick) =>
    pick > 1 ? `ACCEPTER LES ${pick} CRÉNEAUX` : "ACCEPTER LA PROPOSITION"

export const DECLINE_BUTTON_LABEL = "JE NE PEUX PAS"

/**
 * Ce que « Je ne peux pas » déclenche, dit par le serveur avant que le tuteur réponde (le repli
 * de la famille) ; à défaut, la conséquence qui vaut toujours : un autre tuteur prend ce qui change.
 */
export const declineConsequence = (offer) =>
    offer?.decline_consequence ||
    "Si tu ne peux pas, un autre tuteur prend ce changement."

export const DECLINE_CONFIRM_TITLE = "Tu ne peux pas ?"
export const DECLINE_BACK_LABEL = "RETOUR"

/** Radio when one créneau is to pick, checkboxes otherwise. */
export const isSingleChoice = (offer) => pickOf(offer) === 1

/** Nothing to choose when the offer holds no more créneaux than it asks: all are preselected. */
export const initialSelection = (offer) => {
    const slots = slotsOf(offer)
    return slots.length > 0 && slots.length <= pickOf(offer)
        ? slots.map((slot) => slot.live_session_id)
        : []
}

/** Toggles a créneau: replaces it for a single choice, never goes past `pick` otherwise. */
export const toggleSlot = (selected, liveSessionId, pick) => {
    if (selected.includes(liveSessionId))
        return selected.filter((id) => id !== liveSessionId)
    if (pick === 1) return [liveSessionId]
    if (selected.length >= pick) return selected
    return [...selected, liveSessionId]
}

export const canAccept = (selected, pick) => pick > 0 && selected.length === pick

export const buildAcceptBody = (selected) => ({
    status: "accepted",
    live_session_ids: [...selected],
})

export const buildDeclineBody = () => ({ status: "rejected" })
